// Keep the public website and portal policies in sync. Technical diagnostics
// belong in logs; HTTP status and field errors remain available to callers.
const statusMessages: Record<number, string> = {
  400: 'Please check the information you entered and try again.',
  401: 'Please sign in again to continue.',
  403: 'You do not have access to this action. Contact SRS if you need help.',
  404: 'This item is no longer available. Refresh the page or return to the list.',
  405: 'This action is not available here. Refresh the page and try again.',
  408: 'This is taking longer than expected. Check your connection and try again.',
  409: 'This item has changed. Refresh the page before trying again.',
  413: 'This file is too large. Choose a smaller file and try again.',
  419: 'Your session has expired. Refresh the page and sign in again.',
  422: 'Please check the highlighted information and try again.',
  423: 'This item is being reviewed and cannot be changed right now.',
  429: 'You have made several attempts. Please wait a moment and try again.',
  500: 'We could not complete this action. Please try again shortly. Contact SRS if it keeps happening.',
  502: 'SRS is temporarily unavailable. Please try again shortly.',
  503: 'SRS is temporarily unavailable. Please try again shortly.',
  504: 'This is taking longer than expected. Please try again shortly.',
};
const technical = /SQLSTATE|PDOException|\b[A-Za-z]+Exception\b|stack trace|vendor[\\/]|(?:app|src)[\\/].+\.(?:php|tsx?)|undefined is not|Cannot read properties|Request failed with status|Failed to fetch|Network(?:Error| Error)|^Unauthenticated\.?$|^Unauthori[sz]ed\.?$|^Forbidden\.?$|^Not Found\.?$|^Server error\.?|^Internal Server Error|No query results|Route \[|route could not be found|permission[:.]|<!doctype|<html|"exception"|"trace"/i;
const labels: Record<string, string> = { property_id: 'property', inspector_id: 'inspector', requester_id: 'requester', access_contact_id: 'access contact', scheduled_at: 'appointment', due_at: 'due date', risk_result: 'risk result', gps_target_lat: 'property latitude', gps_target_lng: 'property longitude', declared_no_conflict: 'conflict declaration', file: 'file' };
function readable(value: unknown, field?: string): string | undefined {
  if (typeof value !== 'string' || !value.trim() || value.length > 600 || technical.test(value)) return undefined;
  let message = value.trim();
  if (field) {
    const label = labels[field] ?? field.replaceAll('_', ' ').replace(/\.\d+\./g, ' ');
    message = message.replaceAll(field, label).replaceAll(field.replaceAll('_', ' '), label);
  }
  return message;
}
type ErrorShape = { response?: { status?: number; data?: { message?: unknown; error?: unknown; errors?: Record<string, unknown> } }; status?: number; apiBody?: { message?: unknown; error?: unknown; errors?: Record<string, unknown> }; code?: string; name?: string; message?: unknown };
export function userErrorMessage(error: unknown, fallback = 'We could not complete this action. Please try again.'): string {
  if (typeof error === 'string') return readable(error) ?? fallback;
  if (!error || typeof error !== 'object') return fallback;
  const e = error as ErrorShape;
  const status = e.response?.status ?? e.status;
  const body = e.response?.data ?? e.apiBody;
  if (status === 405 || status === 419) return statusMessages[status];
  if (status === 403 && /^Invalid signature\.?$/i.test(String(body?.message ?? '').trim())) return 'This access link has expired or is no longer valid. Reopen the file to get a fresh link.';
  if (status && status >= 500) {
    const guidance = readable(body?.message);
    return guidance && /please|try again|contact|unavailable/i.test(guidance) ? guidance : statusMessages[status] ?? statusMessages[500];
  }
  if (e.code === 'ECONNABORTED' || e.code === 'ETIMEDOUT' || e.name === 'AbortError') return statusMessages[408];
  if (e.code === 'ERR_NETWORK' || /Failed to fetch|Network(?:Error| Error)|fetch failed/i.test(String(e.message))) return 'We could not connect to SRS. Check your internet connection and try again.';
  if (status === 422 && body?.errors && typeof body.errors === 'object') {
    for (const [field, values] of Object.entries(body.errors)) {
      const message = readable(Array.isArray(values) ? values[0] : values, field);
      if (message) return message;
    }
  }
  return readable(body?.message) ?? readable(body?.error) ?? readable(e.message) ?? (status ? statusMessages[status] : undefined) ?? fallback;
}
export function normalizeUserError<T>(error: T): T {
  if (!error || typeof error !== 'object') return error;
  const e = error as ErrorShape;
  // Cancellation is control flow, not a failed user action. DOMException.message
  // is read-only in browsers; preserve its identity for cancellation handlers.
  if (e.name === 'AbortError') return error;
  const message = userErrorMessage(error);
  try { e.message = message; }
  catch { return Object.assign(new Error(message), { name: e.name ?? 'Error', status: e.status, code: e.code, response: e.response }) as T; }
  if (e.response?.data && typeof e.response.data === 'object') {
    e.response.data.message = message;
    const fields = e.response.data.errors;
    if (fields) for (const [field, values] of Object.entries(fields)) {
      fields[field] = (Array.isArray(values) ? values : [values]).map(value => readable(value, field) ?? 'Please check this information and try again.');
    }
  }
  return error;
}
